import React from "react";
import moment from "moment";
import { Box, Chip, CircularProgress, Tooltip, Typography } from "@mui/material";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import { AssignmentLate, AssignmentTurnedIn, ShoppingCart } from "@mui/icons-material";
import { useTherapyColorMap } from "../../hooks/useTherapyColorMap";
import { TimeSlotExt } from "./Calendar.type";
import { VisitTypeBadge } from "../../atoms/Chip";
import { InformingBadge } from "../../atoms/InformingBadge";
import { mobiliTheme } from "../../themes/mobiliTheme";
import { adaptAppointmentsToCalendarEvents, CalendarEvent, ClientTimeSlot, isGroupTherapistType } from "./controller/adapters";
import { adaptContinuousAppointmentsToCalendarEvents } from "./controller/adapters/batch.utilities";
import { getContrastTextColor } from "../../themes/colorUtils";
import { pickWorstInformingProjection } from "../../helpers/informingDisplay";
import { AppointmentDto } from "../../api-client";
import { eventToClientTimeSlot } from "./controller/adapters/display-to-client/reverse.adapter";
import notificationStore from "../../stores/Notification/notificationStore";

// Tracks participants we have already toasted about so we don't spam on every re-render.
const warnedBadParticipantIds = new Set<string>();

/**
 * "Rx!?" chip for a prescription without a date. Mirrors MobiliCalendar's
 * `.mc-flag-rx` (same crimson, same dense chip metrics) so both calendars flag
 * the case identically; it replaces a DangerousTwoTone stop icon that read as a
 * generic error rather than "check this Rx". forwardRef because MUI's Tooltip
 * needs to attach a ref to its child.
 */
const RxMissingFlag = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  (props, ref) => (
    <Box
      ref={ref}
      component="span"
      {...props}
      sx={{
        display: "inline-block",
        fontSize: "8.5px",
        fontWeight: 800,
        lineHeight: 1.5,
        color: "#fff",
        backgroundColor: "rgb(var(--secondary))",
        borderRadius: "var(--radius-chip, 3px)",
        padding: "0 3px",
        letterSpacing: "0.02em",
        verticalAlign: "middle",
        marginLeft: "1px",
        whiteSpace: "nowrap",
      }}
    >
      Rx!?
    </Box>
  )
);
RxMissingFlag.displayName = "RxMissingFlag";

interface EventContentProps {
  clientTS: ClientTimeSlot;
  onNoteIconClick: (event: React.MouseEvent, timeSlot: TimeSlotExt) => void;
  isLocked?: boolean;
  onGroupParticipantPatientClick?: (calendarEvent: CalendarEvent, event: React.MouseEvent) => void;
  onDocumentIconClick?: (event: React.MouseEvent, timeSlot: TimeSlotExt) => void;
  treatmentNotes?: string;
}

/**
 * Helper functions for event classification based on ClientTimeSlot
 */
const isNonAppointmentTimeSlot = (clientTS: ClientTimeSlot): boolean => {
  return (
    !clientTS.type ||
    clientTS.type === "EventTimeSlot" ||
    clientTS.type === "LeaveOfAbsence" ||
    clientTS.type === "LunchBreak" ||
    clientTS.type === "PublicHoliday" ||
    clientTS.type === "ContinuousEventTimeSlot"
  );
};

// A passive preparatory application renders as a background band rather than
// a block, because the therapist is free during it.
const isBackgroundTreatment = (clientTS: ClientTimeSlot): boolean =>
  clientTS.therapy?.isHeatTreatment === true;

const formatTime = (date: Date): string => {
  return date ? moment(date).format("HH:mm") : "?";
};

/**
 * Minimum slot length (minutes) required to show the bottom-anchored end-time label.
 * Anything shorter isn't tall enough to fit it without landing on top of the top
 * start-time label during drag/resize. 11 keeps the prior behaviour (hide slots of
 * 10 minutes or less), matching the `> 10` heuristic used for the start-time line break.
 */
const MIN_MINUTES_FOR_END_LABEL = 11;

/**
 * Treatment appointments need more vertical room before the end-time label is legible,
 * so they use a higher threshold than the generic slot minimum.
 */
const MIN_MINUTES_FOR_TREATMENT_END_LABEL = 25;

/** Absolutely-positioned end-time label shown at the bottom of events during drag/resize */
const EndTimeLabel: React.FC<{
  start?: Date;
  end?: Date;
  minMinutes?: number;
  color?: string;
}> = ({ start, end, minMinutes = MIN_MINUTES_FOR_END_LABEL, color }) => {
  if (!start || !end) return null;
  // Skip the bottom label on short slots (e.g. 5-min event time slots): there is no
  // vertical room, so it would overlap the start time and read as the time twice.
  if (moment(end).diff(moment(start), "minutes") < minMinutes) return null;
  return (
    <div
      className="event-drag-end-time"
      style={{
        position: "absolute",
        bottom: 0,
        left: 2,
        pointerEvents: "none",
        ...(color ? { color } : {}),
      }}
    >
      {moment(end).format("HH:mm")}
    </div>
  );
};

/**
 * EventContent Component
 *
 * Renders calendar event content based on the provided ClientTimeSlot.
 * Handles different event types (timeslots, appointments, background treatments)
 * and displays appropriate UI elements, icons, and styling.
 */
export const EventContent: React.FC<EventContentProps> = ({
  clientTS,
  onNoteIconClick,
  isLocked = false,
  onGroupParticipantPatientClick,
  onDocumentIconClick,
  treatmentNotes,
}) => {
  // Catalog-sourced colour map (24h-cached reference data), theme fallback.
  const therapyColorMap = useTherapyColorMap();
  // MD-232: keyed by catalog identity (TherapyMethod / PrescribedTherapy id),
  // never by the abbreviation being rendered — that is renameable display text,
  // and keying on it dropped renamed Heilmittel to grey (D-RX-05).
  const therapyColor = (colorKey: string | undefined) =>
    (colorKey && therapyColorMap[colorKey]) || "grey";
  const startTime = formatTime(clientTS.start);
  const therapyAbbr = clientTS.therapy?.abbreviation;
  const therapyColorId = clientTS.therapy?.colorKey;
  const isHeatTreatment = clientTS.containsHeatTreatment;
  const displayTitle = clientTS.title || clientTS.patient?.displayName || "";
  // console.debug("[EventContent]:", clientTS.title, clientTS);
  if (isGroupTherapistType(clientTS.type)) {
    const participantTherapy = (clientTS?.participants?.length
      ? clientTS.participants[0]?.timeSlots?.[0]?.therapy
      : undefined) as { abbreviation?: string; colorKey?: string } | undefined;
    const therapyAbbr =
      clientTS?.participants && clientTS?.participants.length > 0
        ? participantTherapy?.abbreviation || "Patient"
        : clientTS.therapy?.abbreviation || "Therapist";
    // The label above falls back to literal "Patient"/"Therapist" text, which is
    // not a catalog key — so the colour is resolved from identity separately.
    const therapyColorId = participantTherapy?.colorKey ?? clientTS.therapy?.colorKey;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          height: "100%",
          overflow: "hidden", // Prevent content from overflowing the calendar event bounds
          position: "relative",
        }}
      >
        {clientTS.isCancelled && <CancelledOverlay />}
        {/* Left content area */}
        <div
          style={{
            flex: 1,
            position: "relative",
            paddingRight: "1px",
            minWidth: 0, // Allow shrinking below content size
            overflow: "hidden", // Contain overflow within this area
          }}
        >
          <Typography component="div" sx={{ lineHeight: 1, wordBreak: "break-all" }}>
            {/* First line: Time text, therapy, treatment index, icons */}
            <div style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap", overflow: "hidden" }}>
              {/* Time text */}
              <b
                className="event-drag-start-time"
                style={{
                  color: clientTS.isCancelled ? "grey" : mobiliTheme.mainHeader,
                  textDecoration: clientTS.isCancelled ? "line-through" : "none",
                }}
              >
                {startTime}
              </b>

              {/* Therapy abbreviation - inline styled instead of Chip */}
              {therapyAbbr && (
                <>
                  <span
                    style={{
                      display: "inline",
                      padding: "1px",
                      margin: "1px",
                      borderRadius: "2px",
                      fontSize: "0.9em",
                      backgroundColor: therapyColor(therapyColorId),
                      color: getContrastTextColor(therapyColor(therapyColorId)),
                    }}
                  >
                    {therapyAbbr}
                  </span>
                  <span>
                    {/* No sublocality strip behind this badge — it sits on the
                        event body, so it takes the default mainHeader color like
                        everywhere else. Contrasting against the sublocality color
                        (as the single-appointment strip does) turned it white. */}
                    <VisitTypeBadge
                      visitType="praxis"
                      visitFee={clientTS.visitFee?.abbreviation}
                    />
                  </span>
                </>
              )}
              {(clientTS?.participants?.length ?? 0) > 3 &&
            (() => {
              const hiddenParticipants = clientTS.participants!.slice(3);
              const hiddenCount = hiddenParticipants.length;
              const hiddenNames = hiddenParticipants
                .map((p: any) => {
                  const last = p?.timeSlots?.[0]?.therapyRx?.rx?.patient?.lastName;
                  const first = p?.timeSlots?.[0]?.therapyRx?.rx?.patient?.firstName;
                  return last && first ? `${last}, ${first}` : "Patient";
                })
                .join("\n");
              return (
                <Tooltip
                  title={
                    <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{hiddenNames}</pre>
                  }
                >
                  <Chip
                    label={`+${hiddenCount}`}
                    size="small"
                    sx={{
                      minHeight: 18,
                      height: 18,
                      fontSize: 11,
                      cursor: "pointer",
                      border: "1px solid #858282ff",
                      borderRadius: "2px",
                      "& .MuiChip-label": {
                        paddingY: 0,
                        paddingX: "4px",
                      },
                    }}
                  />
                </Tooltip>
              );
            })()}
            {/* Group (KGG) informing badge: one worst-case marker for the whole
                card across its up-to-3 participants. KGG is always a Praxis
                visit. Future-only, pinned right, matching the single-appointment
                card. */}
            {clientTS.start &&
              new Date(clientTS.start) > new Date() &&
              (() => {
                const worst = pickWorstInformingProjection(
                  clientTS.participants,
                  clientTS.start,
                  "Praxis",
                );
                if (!worst) return null;
                return (
                  <span
                    style={{
                      marginLeft: "auto",
                      flexShrink: 0,
                      paddingLeft: 4,
                      alignSelf: "flex-start",
                    }}
                  >
                    <InformingBadge
                      projection={worst}
                      currentStart={clientTS.start}
                      visitType="Praxis"
                      currentTherapistId={clientTS.user?.id}
                      currentTherapistName={clientTS.user?.fullName ?? clientTS.user?.displayName}
                      size={14}
                    />
                  </span>
                );
              })()}
            </div>
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.125, marginTop: 0.25 }}>
            {(clientTS?.participants ?? []).slice(0, 3).map((p: any, idx: number) => {
              const patient =
                p.timeSlots?.[0]?.therapyRx?.rx?.patient?.lastName &&
                p.timeSlots?.[0]?.therapyRx?.rx?.patient?.firstName
                  ? p.timeSlots[0].therapyRx.rx.patient.lastName +
                    ", " +
                    p.timeSlots[0].therapyRx.rx.patient.firstName
                  : "Patient";
              // Defense-in-depth: never let a single bad participant take down the calendar.
              // The adapter runs inline during render here; any exception inside it would
              // otherwise propagate up through FullCalendar's componentDidMount and produce
              // a white screen for the whole view.
              let calendarEvent: CalendarEvent | undefined;
              let clientClickedTS: ClientTimeSlot | undefined;
              try {
                calendarEvent = clientTS.isContinuous
                  ? adaptContinuousAppointmentsToCalendarEvents([p])[0]
                  : adaptAppointmentsToCalendarEvents([p])[0];
                clientClickedTS = calendarEvent
                  ? eventToClientTimeSlot(calendarEvent)
                  : undefined;
              } catch (err) {
                const participantId = p?.id;
                const firstSlotId = p?.timeSlots?.[0]?.id;
                console.error(
                  "[EventContent] Failed to adapt group participant; skipping chip.",
                  { participantId, firstSlotId, error: err, participant: p },
                );
                const dedupKey = `${participantId ?? "?"}:${firstSlotId ?? "?"}`;
                if (!warnedBadParticipantIds.has(dedupKey)) {
                  warnedBadParticipantIds.add(dedupKey);
                  notificationStore.showMessage(
                    `⚠️ Gruppen-Teilnehmer konnte nicht angezeigt werden (Termin ${participantId ?? "?"}, Zeitslot ${firstSlotId ?? "?"}). Bitte Datensatz prüfen.`,
                    "warning",
                  );
                }
              }
              if (!calendarEvent || !clientClickedTS) {
                return null;
              }
              return (
                <div key={idx} data-participant-chip data-participant-index={idx}>
                  <Chip
                    className={clientClickedTS.attended ? "attended-event" : ""}
                    label={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            color: mobiliTheme.mainHeader,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "120px",
                          }}
                        >
                          {patient}
                        </span>

                        {/* Icons */}
                        {!clientClickedTS.rx?.date && !clientClickedTS.rx?.rxNumber && (
                          <ShoppingCart fontSize="small" sx={{ color: "grey" }} />
                        )}

                        {!clientClickedTS.rx?.date && !clientClickedTS.isContinuous && (
                          <Tooltip
                            title={
                              "Verordnung " +
                              (clientClickedTS.rx?.rxNumber || "") +
                              JSON.stringify(clientClickedTS.rx)
                            }
                          >
                            <RxMissingFlag />
                          </Tooltip>
                        )}

                        {isHeatTreatment && (
                          <Tooltip title="Warmpackung inklusive">
                            <LocalFireDepartmentIcon
                              fontSize={"small"}
                              style={{
                                color: mobiliTheme.palette.secondary.main,
                                verticalAlign: "middle",
                                marginLeft: "1px",
                              }}
                            />
                          </Tooltip>
                        )}

                        {clientClickedTS.hasNotes && (
                          <Tooltip title="Terminhinweise">
                            <span onClick={(e) => onNoteIconClick(e, clientClickedTS as any)}>
                              <NoteAltOutlinedIcon
                                fontSize="small"
                                sx={{ cursor: "pointer", color: mobiliTheme.palette.info.main }}
                              />
                            </span>
                          </Tooltip>
                        )}

                        {/* Compact treatment index (compressed, not vertical) */}
                        {clientClickedTS.treatmentIndex && (
                          <span
                            style={{
                              display: "inline-block",
                              fontSize: "0.66em",
                              marginLeft: "1px",
                              lineHeight: 1,
                              textAlign: "center",
                              verticalAlign: "middle",
                              color: "black",
                            }}
                          >
                            <span style={{ borderBottom: "1px solid", display: "block" }}>
                              {clientClickedTS.treatmentIndex.split("/")[0]}
                            </span>
                            <span style={{ display: "block" }}>
                              {clientClickedTS.treatmentIndex.split("/")[1]}
                            </span>
                          </span>
                        )}

                        {/* Status flags */}
                        {(clientClickedTS.isContinuous || clientClickedTS.rx?.isPerpetual) && (
                          <span style={{ color: "blue", fontSize: "0.8em" }}>∞</span>
                        )}
                        {clientClickedTS.attended &&
                          (clientClickedTS.wasDocumentedCount &&
                          clientClickedTS.wasDocumentedCount > 0 ? (
                            <Tooltip
                              title={
                                <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                                  {treatmentNotes}
                                </pre>
                              }
                            >
                              <AssignmentTurnedIn
                                fontSize="small"
                                sx={{ color: "success.main", ml: 0.2 }}
                                onMouseEnter={(e) =>
                                  onDocumentIconClick &&
                                  onDocumentIconClick(e, clientClickedTS as any)
                                }
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Dokumentation fehlt">
                              <AssignmentLate
                                fontSize="small"
                                sx={{ color: "error.main", ml: 0.2 }}
                              />
                            </Tooltip>
                          ))}
                      </div>
                    }
                    sx={{
                      minHeight: 24,
                      fontSize: 12,
                      cursor: "grab",
                      width: "100%",
                      border: "1px solid #858282ff",
                      borderRadius: "2px",
                      ...(clientClickedTS.attended
                        ? {
                            background:
                              "repeating-linear-gradient(-45deg, transparent 0 5px, rgb(191, 191, 191) 5px 10px);",
                          }
                        : {}),
                      "& .MuiChip-label": {
                        paddingY: 0,
                        paddingX: "1px",
                      },
                    }}
                    onClick={(e: any) => {
                      onGroupParticipantPatientClick &&
                        onGroupParticipantPatientClick(calendarEvent, e);
                    }}
                  />
                </div>
              );
            })}
          </Box>
          <EndTimeLabel start={clientTS.start} end={clientTS.end} />
        </div>
      </div>
    );
  }
  // BACKGROUND TREATMENT
  else if (isBackgroundTreatment(clientTS)) {
    return (
      <div style={{ position: "relative", height: "100%" }}>
        {clientTS.isCancelled && <CancelledOverlay />}
        <b className="event-drag-start-time" style={{ color: clientTS.isCancelled ? "grey" : "default" }}>{startTime}</b> <br />
        <i style={{ color: clientTS.isCancelled ? "grey" : "default" }}>{displayTitle}</i> <br />
        <span style={{ color: clientTS.isCancelled ? "grey" : "default" }}>{therapyAbbr}</span>{" "}
        <br />
        <EndTimeLabel start={clientTS.start} end={clientTS.end} />
      </div>
    );
  } else {
    if (isNonAppointmentTimeSlot(clientTS)) {
      // TIMESLOTs ( not appointments) ... eg. EventTimeSlots and lunchbreaks
      const slotBGColor =
        clientTS.type === "LunchBreak"
          ? mobiliTheme.palette.secondary.light
          : "white";
      return (
        <div style={{ position: "relative", flex: 1, height: "100%" }}>
          <>
            {/* TS,Tr */}
            <Typography component="div" sx={{ lineHeight: 1, wordBreak: "break-all" }}>
              <b className="event-drag-start-time" style={{ color: clientTS.isCancelled ? "grey" : "default" }}>{startTime}</b>
              {moment(clientTS.end).diff(moment(clientTS.start), "minutes") > 10 && <br />}
              <span
                style={{
                  display: "inline",
                  padding: "0 4px",
                  borderRadius: "2px",
                  fontSize: 12,
                }}
              >
                {clientTS.type === "LunchBreak" ? "Pause" : displayTitle}
                {clientTS.isContinuous && <span style={{ color: "white" }}> ∞</span>}
              </span>
            </Typography>
          </>
          <EndTimeLabel start={clientTS.start} end={clientTS.end} />
        </div>
      );
    } else if (clientTS.appointmentId) {
      // checking whether it belongs to an appointment
      const subLocColor = clientTS.sublocality?.color || "lightgrey";
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            height: "100%",
            overflow: "hidden", // Prevent content from overflowing the calendar event bounds
            position: "relative",
          }}
        >
          {clientTS.isCancelled && <CancelledOverlay />}
          {/* Left content area */}
          <div
            style={{
              flex: 1,
              position: "relative",
              paddingRight: "1px",
              minWidth: 0, // Allow shrinking below content size
              overflow: "hidden", // Contain overflow within this area
            }}
          >
            <Typography component="div" sx={{ lineHeight: 1, wordBreak: "break-all" }}>
              {/* First line: Time text, therapy, treatment index, icons */}
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {/* Left cluster keeps its original inline layout untouched */}
                <span style={{ minWidth: 0, overflow: "hidden", whiteSpace: "nowrap" }}>
                {/* Time text */}
                <b
                  className="event-drag-start-time"
                  style={{
                    color: clientTS.isCancelled ? "grey" : mobiliTheme.mainHeader,
                    textDecoration: clientTS.isCancelled ? "line-through" : "none",
                  }}
                >
                  {startTime}
                </b>
                {!clientTS.rx?.date && !clientTS.rx?.rxNumber ? (
                  <ShoppingCart
                    sx={{
                      color: "grey",
                      verticalAlign: "middle",
                      marginLeft: "1px",
                    }}
                    fontSize={"small"}
                  />
                ) : !clientTS.rx?.date && !clientTS.isContinuous ? (
                  <Tooltip
                    title={
                      "Verordnung " + (clientTS.rx?.rxNumber || "") + JSON.stringify(clientTS.rx)
                    }
                  >
                    <RxMissingFlag />
                  </Tooltip>
                ) : null}
                {/* Icons inline */}
                {isHeatTreatment && (
                  <Tooltip title="Warmpackung inklusive">
                    <LocalFireDepartmentIcon
                      fontSize={"small"}
                      style={{
                        color: mobiliTheme.palette.secondary.main,
                        verticalAlign: "middle",
                        marginLeft: "1px",
                      }}
                    />
                  </Tooltip>
                )}
                {clientTS.hasNotes && (
                  <Tooltip title="Terminhinweise">
                    <span onClick={(e) => onNoteIconClick(e, clientTS as any)}>
                      <NoteAltOutlinedIcon
                        fontSize={"small"}
                        sx={{
                          cursor: "pointer",
                          verticalAlign: "middle",
                          marginLeft: "2px",
                          color: mobiliTheme.palette.info.main,
                        }}
                      />
                    </span>
                  </Tooltip>
                )}
                {/* Therapy abbreviation - inline styled instead of Chip */}
                {therapyAbbr && (
                  <span
                    style={{
                      display: "inline",
                      padding: "1px",
                      margin: "1px",
                      borderRadius: "2px",
                      fontSize: "0.9em",
                      backgroundColor: therapyColor(therapyColorId),
                      color: getContrastTextColor(therapyColor(therapyColorId)),
                    }}
                  >
                    {therapyAbbr}
                  </span>
                )}
                {/* Treatment index as inline element */}
                {clientTS.treatmentIndex && (
                  <span
                    style={{
                      display: "inline-block",
                      fontSize: "0.6em",
                      marginLeft: "1px",
                      lineHeight: 1,
                      textAlign: "center",
                      verticalAlign: "middle",
                      color: "black",
                    }}
                  >
                    <span style={{ borderBottom: "1px solid", display: "block" }}>
                      {clientTS.treatmentIndex.split("/")[0]}
                    </span>
                    <span style={{ display: "block" }}>
                      {clientTS.treatmentIndex.split("/")[1]}
                    </span>
                  </span>
                )}{" "}
                {/* Cancellation reason */}
                {clientTS.isCancelled && clientTS.reason && (
                  <span
                    style={{
                      fontSize: "0.8em",
                      color: "grey",
                      textDecoration: "line-through",
                    }}
                  >
                    *{clientTS.reason}
                  </span>
                )}{" "}
                {(clientTS.isContinuous || clientTS.rx?.isPerpetual) && (
                  <span style={{ color: "blue" }}>∞</span>
                )}
                </span>
                {/* Informing badge is only meaningful for upcoming appointments;
                    keep past appointments visually unchanged (no marker).
                    Pinned to the right so it stays in a stable, scannable slot. */}
                {clientTS.start && new Date(clientTS.start) > new Date() && (
                  <span
                    style={{
                      marginLeft: "auto",
                      flexShrink: 0,
                      paddingLeft: 4,
                      alignSelf: "flex-start",
                      marginTop: 0,
                    }}
                  >
                    <InformingBadge
                      projection={clientTS.informing}
                      currentStart={clientTS.start}
                      visitType={clientTS.visitType}
                      currentTherapistId={clientTS.user?.id}
                      currentTherapistName={clientTS.user?.fullName ?? clientTS.user?.displayName}
                      size={14}
                    />
                  </span>
                )}
              </div>
              {/* Second line: Patient name */}
              <div
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: mobiliTheme.mainHeader,
                  display: "flex",
                  alignItems: "center",
                  gap: 2, // small gap between text and icon
                }}
              >
                <span
                  style={{
                    display: "inline",
                    padding: "1px 2px",
                    fontSize: 12,
                    lineHeight: 1.2,
                    color: mobiliTheme.mainHeader,
                  }}
                >
                  {displayTitle}
                </span>

                {clientTS.attended &&
                  (clientTS.wasDocumentedCount && clientTS.wasDocumentedCount > 0 ? (
                    <Tooltip
                      title={
                        <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{treatmentNotes}</pre>
                      }
                    >
                      <AssignmentTurnedIn
                        fontSize="small"
                        sx={{ color: "success.main", ml: 0.2 }}
                        onMouseEnter={(e) =>
                          onDocumentIconClick && onDocumentIconClick(e, clientTS as any)
                        }
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Dokumentation fehlt">
                      <AssignmentLate fontSize="small" sx={{ color: "error.main", ml: 0.2 }} />
                    </Tooltip>
                  ))}
              </div>
            </Typography>
            {/* Overlay spinner if locked */}
            {isLocked && <LockedOverlay />}
            <EndTimeLabel
              start={clientTS.start}
              end={clientTS.end}
              minMinutes={MIN_MINUTES_FOR_TREATMENT_END_LABEL}
              color="black"
            />
          </div>
          {/* Right content area */}
          <div
            style={{
              minWidth: "30px", // Ensure minimum 30px width
              maxWidth: "30px", // Prevent growing beyond necessary
              flexShrink: 0, // Prevent shrinking below minWidth
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              backgroundColor: subLocColor,
            }}
          >
            <div style={{ flex: 1 }}>
              {clientTS.visitType && (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <VisitTypeBadge
                    visitType={clientTS.visitType}
                    visitFee={clientTS.visitFee?.abbreviation}
                    sx={{ color: getContrastTextColor(subLocColor) }}
                  />
                  <Typography
                    fontSize={
                      clientTS.sublocality?.abbreviation?.length
                        ? clientTS.sublocality.abbreviation.length > 4
                          ? 8
                          : 11
                        : 11
                    }
                    lineHeight={1}
                    color={getContrastTextColor(subLocColor)}
                  >
                    {clientTS.sublocality?.abbreviation}
                  </Typography>
                </Box>
              )}
            </div>
          </div>
        </div>
      );
    }
  }

  if (clientTS.type === "WorkingHours") {
    return (
      <div style={{ position: "relative", flex: 1, height: "100%" }}>
        <Typography sx={{ lineHeight: 1, wordBreak: "break-all" }}>
          <b className="event-drag-start-time">{startTime}</b>
          {moment(clientTS.end).diff(moment(clientTS.start), "minutes") > 10 && <br />}
          <span
            style={{
              display: "inline",
              padding: "0 4px",
              borderRadius: "2px",
              fontSize: 12,
            }}
          >
            {displayTitle}
          </span>
        </Typography>
        <EndTimeLabel start={clientTS.start} end={clientTS.end} />
      </div>
    );
  }

  // Fallback for other event types
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        backgroundColor: "grey",
        position: "relative",
        height: "100%",
      }}
    >
      <b className="event-drag-start-time">{startTime}</b>
      {displayTitle}
      {/* Overlay spinner if locked */}
      {isLocked && <LockedOverlay />}
      <EndTimeLabel start={clientTS.start} end={clientTS.end} />
    </div>
  );
};

/**
 * LockedOverlay Component
 *
 * Displays a loading spinner overlay for locked events.
 * Extracted as a separate component for reusability.
 */
const CancelledOverlay: React.FC = () => (
  <div
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(255, 255, 255, 0.4)",
      zIndex: 5,
      pointerEvents: "none",
    }}
  />
);

const LockedOverlay: React.FC = () => (
  <div
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(255, 255, 255, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10,
    }}
  >
    <CircularProgress size={24} />
  </div>
);
